import { Component, OnDestroy, OnInit, ViewEncapsulation } from '@angular/core';

import { Subscription } from 'rxjs';

import { Alert } from '../../../models/alert.model';
import { AlertEnum } from '../messages.enum';
import { MessageService } from '../message.service';

@Component({
    selector: 'alert-timer',
    templateUrl: './alert-timer.component.html',
    styleUrls: ['./alert-timer.component.scss'],
    standalone: true,
    encapsulation: ViewEncapsulation.None,
})
export class AlertTimerComponent implements OnInit, OnDestroy {
  /** Alerta actual */
  alert: Alert | null = null;

  /** Estado UI */
  visible = false;

  /** Cuenta regresiva */
  totalSeconds = 0;
  remainingSeconds = 0;
  private intervalId: ReturnType<typeof setInterval> | null = null;

  private readonly subscriptions = new Subscription();

  constructor(private readonly messageService: MessageService) {}

  ngOnInit(): void {
    this.subscriptions.add(
      this.messageService.startTimerAlert().subscribe((incoming: Alert | null | undefined) => {
        // Un valor falsy indica "limpiar/cerrar"
        if (!incoming) {
          this.reset(false);
          return;
        }

        const alert = new Alert(incoming);
        this.applyAlertVisuals(alert);
        this.startCountdown(alert);
      }),
    );
  }

  ngOnDestroy(): void {
    this.subscriptions.unsubscribe();
    this.clearInterval();
  }

  /** Texto MM:SS */
  get countdownText(): string {
    const m = Math.floor(this.remainingSeconds / 60);
    const s = Math.max(0, this.remainingSeconds % 60);
    return `${m.toString().padStart(2, '0')}:${s.toString().padStart(2, '0')}`;
  }

  /** Progreso para barra (0-100) */
  get progressPercent(): number {
    if (!this.totalSeconds) return 0;
    return Math.max(0, Math.min(100, (this.remainingSeconds / this.totalSeconds) * 100));
  }

  /** Cerrar por interacción del usuario */
  cancelAlert(): void {
    this.messageService.stopTimerAlert(true);
    this.reset(true);
  }

  private startCountdown(alert: Alert): void {
    this.clearInterval();

    const seconds = Number(alert.timer ?? 0);
    this.totalSeconds = seconds > 0 ? seconds : 60;
    this.remainingSeconds = this.totalSeconds;

    this.alert = alert;
    this.visible = true;

    this.intervalId = window.setInterval(() => {
      this.remainingSeconds = Math.max(0, this.remainingSeconds - 1);

      if (this.remainingSeconds <= 0) {
        // Tiempo terminado
        this.messageService.stopTimerAlert(false);
        this.reset(false);
      }
    }, 1000);
  }

  private reset(canceled: boolean): void {
    this.clearInterval();
    this.visible = false;
    this.alert = null;
    this.totalSeconds = 0;
    this.remainingSeconds = 0;
  }

  private clearInterval(): void {
    if (this.intervalId) {
      clearInterval(this.intervalId);
      this.intervalId = null;
    }
  }

  private applyAlertVisuals(alert: Alert): void {
    switch (alert.type) {
      case AlertEnum.Success:
        alert.icon = 'pi pi-check';
        alert.class = 'success';
        break;
      case AlertEnum.Warning:
        alert.icon = 'pi pi-exclamation-triangle';
        alert.class = 'warning';
        break;
      case AlertEnum.Error:
        alert.icon = 'pi pi-times-circle';
        alert.class = 'error';
        break;
      case AlertEnum.Information:
        alert.icon = 'pi pi-info-circle';
        alert.class = 'info';
        break;
      default:
        alert.icon = 'pi pi-info-circle';
        alert.class = 'info';
        break;
    }
  }
}
